import React, { useState, useEffect, useMemo } from "react";
import {
  Drawer,
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  IconButton,
  Divider,
  Alert,
  useTheme,
  Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useQuery } from "@apollo/client";
import { GET_CUSTOMER_INTEGRATIONS } from "../../../../apis/settingsApis/integration-apis/integrationApis";
import GCPGuide from "../../../Organization/InfraManagement/InfraTemplateDialog/guides/GCPGuide";
import gcpLogo from "../../../../Assets/Images/gcp.png";

const MANDATORY_FIELDS = [
  "client_id", // added client_id as mandatory
  "project_id",
  "private_key_id",
  "private_key",
  "client_email",
  "token_uri",
  "type",
  "universe_domain",
];

const DEFAULT_TOKEN_URI = "https://oauth2.googleapis.com/token";
const DEFAULT_ACCOUNT_TYPE = "service_account";
const DEFAULT_UNIVERSE_DOMAIN = "googleapis.com";

const GCPConnectionDrawer = ({
  open,
  onClose,
  refetchIntegrationDetails,
  onStoreIntegrationDetails,
}) => {
  const theme = useTheme();

  const [fields, setFields] = useState({
    project_id: "",
    private_key_id: "",
    private_key: "",
    client_email: "",
    client_id: "",
    token_uri: DEFAULT_TOKEN_URI,
    type: DEFAULT_ACCOUNT_TYPE,
    universe_domain: DEFAULT_UNIVERSE_DOMAIN,
  });

  const [jsonError, setJsonError] = useState(null);
  const [jsonData, setJsonData] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const updateField = (field, value) => {
    setFields((prev) => ({ ...prev, [field]: value }));
  };

  const validateFields = () =>
    MANDATORY_FIELDS.every((f) => fields[f]?.trim().length > 0);

  const handleJsonUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);

        const missing = MANDATORY_FIELDS.filter((f) => !parsed[f]);
        if (missing.length > 0) {
          setJsonError(`Missing required fields: ${missing.join(", ")}`);
          return;
        }

        setJsonError(null);
        setJsonData(parsed);

        setFields({
          project_id: parsed.project_id,
          private_key_id: parsed.private_key_id,
          private_key: parsed.private_key,
          client_email: parsed.client_email,
          token_uri: parsed.token_uri || DEFAULT_TOKEN_URI,
          client_id: parsed.client_id,
          // defaults if missing
          type: parsed?.type || DEFAULT_ACCOUNT_TYPE,
          universe_domain: parsed?.universe_domain || DEFAULT_UNIVERSE_DOMAIN,
        });
      } catch (err) {
        setJsonError("Invalid JSON file");
      }
    };
    reader.readAsText(file);
  };

  const {
    data: customerIntegrationsResponse,
    loading: loadingCustomerIntegrations,
    refetch: refetchCustomerIntegrations,
  } = useQuery(GET_CUSTOMER_INTEGRATIONS);

  const [isIntegrationActive, setIsIntegrationActive] = useState(undefined);

  useEffect(() => {
    if (customerIntegrationsResponse) {
      const integration =
        customerIntegrationsResponse?.getAccountDetails?.userIntegrationsDetails?.list?.find(
          (item) => item.integration_key === "gcp"
        );
      setIsIntegrationActive(integration?.is_active || false);
    }
  }, [customerIntegrationsResponse]);

  const handleSubmit = async () => {
    const integrationResourceId = JSON.stringify(fields);

    if (!onStoreIntegrationDetails) {
      console.error("onStoreIntegrationDetails is not provided");
      return;
    }

    setSubmitting(true);
    try {
      const result = await onStoreIntegrationDetails({
        integratedAccountId: integrationResourceId,
        integrationKey: "gcp",
        integrationType: "GCP",
        sync: true,
      });

      if (result?.success) {
        onClose();
      }
    } catch (error) {
      console.error("Error updating integration details:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      TransitionComponent={useMemo(
        () => (props) => <Slide {...props} direction="left" />,
        []
      )}
      PaperProps={{
        sx: {
          width: "75%",
          maxWidth: "1400px",
          backgroundColor: theme.customColors.background.main,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#FFFFFF",
          borderBottom: `1px solid ${theme.customColors.border.subtle}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 4,
            py: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "10px",
                backgroundColor: theme.customColors.background.cardHeader,
                border: `1px solid ${theme.customColors.border.main}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={gcpLogo}
                alt="GCP"
                style={{ width: 32, height: 32, objectFit: "contain" }}
              />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                Connect Google Cloud Platform
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Follow the steps below to establish a secure connection
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ px: 4, py: 3, overflowY: "auto" }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
          Setup Guide
        </Typography>

        <GCPGuide />

        <Divider sx={{ my: 4 }} />

        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Required Credentials
        </Typography>

        <Grid container spacing={2}>
          {MANDATORY_FIELDS.map((field) => (
            <Grid item xs={12} sm={6} key={field}>
              <TextField
                fullWidth
                label={field}
                value={fields[field] || ""}
                onChange={(e) => updateField(field, e.target.value)}
              />
            </Grid>
          ))}
        </Grid>
        <Divider sx={{ my: 3 }}>OR Upload JSON</Divider>

        {/* NEW JSON DESCRIPTION ADDED HERE */}
        <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
          If you downloaded the JSON key file in <b>Step 7</b>, you can upload
          it here. We will automatically read the file and fill all required
          fields for you. Please make sure the file has not been modified after
          download.
        </Typography>

        <Button variant="contained" component="label">
          Upload JSON
          <input
            type="file"
            hidden
            accept="application/json"
            onChange={handleJsonUpload}
          />
        </Button>

        {jsonError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {jsonError}
          </Alert>
        )}

        {jsonData && (
          <pre style={{ background: "#f5f5f5", padding: 10, marginTop: 20 }}>
            {JSON.stringify(jsonData, null, 2)}
          </pre>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          backgroundColor: "#fff",
          borderTop: `1px solid ${theme.customColors.border.subtle}`,
          px: 4,
          py: 3,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Button onClick={onClose}>Cancel</Button>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!validateFields() || loadingCustomerIntegrations || submitting}
          startIcon={<CheckCircleIcon />}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: "white",
            textTransform: "none",
            px: 4,
            py: 1.5,
            fontWeight: 600,
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: theme.customColors.button.hover,
            },
            "&:disabled": {
              backgroundColor: theme.customColors.background.disabled,
              color: theme.customColors.text.disabled,
            },
          }}
        >
          {submitting ? "Connecting..." : "Connect Integration"}
        </Button>
      </Box>
    </Drawer>
  );
};

export default GCPConnectionDrawer;
